import {
  // eslint-disable-next-line no-restricted-imports
  useNavigate as useReactRouterNavigate,
  generatePath,
} from 'react-router-dom';
import React from 'react';

import { dialogEntriesOnTop } from '../../components/DialogBrowserBack/startDialogBrowserBack';
import { type Routes } from '../types';

export default function useNavigate() {
  const reactNavigate = useReactRouterNavigate();
  const navigate = React.useCallback(
    (
      path: '..' | '/' | -1 | Routes,
      options?: {
        // `string`, not `string | undefined`: a params bag holding an undefined
        // value makes `generatePath` throw `Missing ":x" param` from inside the
        // click handler — an uncaught error that leaves the control dead
        // (Sentry MYFITWORLD-V2-6X / -6M / -4K). It used to be permitted here
        // and then cast away with `as any`. For the org-vs-personal decision
        // use `orgAwareRoute`, which returns the path and the matching params
        // from one choice.
        params?: Record<string, string>;
        state?: Record<string, unknown>;
        replace?: boolean;
        search?: string;
      },
    ) => {
      if (!path) throw new Error('Trying to access unrelated route.');
      if (path === -1) {
        // the page before this one, past the entries of any dialog this is
        // called from ("Quit & Save" in the player)
        return reactNavigate(-1 - dialogEntriesOnTop());
      }
      // the query string is appended AFTER the params are filled in: passing
      // `:param?query` to generatePath makes it read the trailing `?` as the
      // optional-param marker, so a path ENDING in a param came out unresolved
      // `<string>`, not inferred: `path`'s declared type is the WHOLE `Routes`
      // union, so leaving the generic to infer makes `PathParam<Path>`
      // distribute over every route in the app and require every route's
      // param name — which one it complains about missing shifts any time
      // the route tree changes shape elsewhere. Pinning `Path` to `string`
      // sidesteps that; `params` stays the intentionally loose bag above.
      const generated = generatePath<string>(path, options?.params);
      return reactNavigate(
        options?.search ? `${generated}?${options.search}` : generated,
        {
          replace: options?.replace,
          state: { search: options?.search, ...options?.state },
        },
      );
    },
    [reactNavigate],
  );

  return navigate;
}
